// F8 (her queue) — **PUSH DELIVERY ANALYTICS: THE DAILY GUIDANCE SENDS, PER DAY.**
//
// Her words: *"daily guidance sends per day — attempted, delivered, failed, the success rate…
// It must READ DT-1b's beacon."* This is the arithmetic half of that sentence, and it is a shared
// `.js` law rather than `src/lib/*.ts` for `strings.js`' own reason: the SERVER composes these
// numbers and the CLIENT draws them, and `src/lib` is client-only. **It imports NOTHING**, which is
// the entry requirement that lets a `.mjs` gate load and RUN every line of it.
//
// >>> THE ROUND IS ONE WORD OF HERS — `failed` — AND THE OBVIOUS READING OF IT IS WRONG EVERY
// MORNING ON THE ROW SHE LOOKS AT FIRST. <<<
//
// `failed = attempted - delivered` is what anybody would write, and it folds together two facts that
// DT-1c already spent a whole round separating (R18): **a send the device never answered** and **a
// send the device has not answered YET.** The beacon's own grace is `UNDELIVERED_AFTER_MINUTES` —
// declared once, in the store, beside the query that applies it — and its reason is written there:
// *"a phone that is off or out of signal is not a defect, and Apple holds a push for a while."*
//
// So on TODAY's row, minutes after a morning run, most of the gap between attempted and delivered is
// phones that are simply asleep. Folded into `failed`, **her healthiest morning reports as a
// catastrophe, every single day, on the newest row — the one she reads first** (R8-4's silent
// direction wearing a loud number). Three outcomes, never two:
//
//   * `delivered` — the DEVICE answered (`received_at`). Never the push service's 201, which is the
//                   whole reason this table exists rather than a counter.
//   * `failed`    — silent, and past the grace. This is DT-1c's `undelivered` verdict counted.
//   * `waiting`   — silent, and inside the grace. Not news, and not a failure.
//
// >>> AND THE RATE IS A SHARE OF WHAT HAS SETTLED, NEVER OF WHAT WAS ATTEMPTED. <<< The same defect
// one column over: `delivered / attempted` on today's row divides this morning's arrivals by this
// morning's whole roster while half of it is still in flight, so the success rate she judges the
// rail by drops every morning and climbs back by lunchtime. **A day with nothing settled has NO
// rate** — `0%` there reads as total failure, which is SURFACE 2(b)'s own ruling about a percentage
// whose denominator cannot carry it (R141: no estimate dressed as a count).

// The three outcomes a sent push can be in, declared once and read by the surface, the arithmetic
// and the fixture. A fourth would have to join here rather than by somebody adding a column.
export const PUSH_DELIVERY_OUTCOMES = ["delivered", "failed", "waiting"];

// >>> A COUNT IS A WHOLE NUMBER OF SENDS OR IT IS NOTHING (R53/R136/R137). <<< `Number(true)` is 1
// and `Number("")` is 0, and either would put a fabricated attempt on a chart she reads the rail by.
// A value this cannot read makes the DAY unreadable rather than making it a zero — because zero is
// an answer here (*nothing was sent that day* is a real and normal fact) and a coerced zero is not.
//
// **AND A FRACTION IS REFUSED RATHER THAN FLOORED, which this round's own fixture is what found.**
// The first draft answered `Math.floor(v)`, so `7.5` came back as `7` — and the row then PARTITIONED
// perfectly, because flooring one count is exactly the edit that keeps the sum looking right while
// half a send disappears. Nothing this reader is handed can legitimately be fractional
// (`count(*)::int`), so a fraction is evidence that something upstream is not what it says it is,
// and the honest answer is *this day could not be read* rather than a number quietly altered to fit.
const count = (v) =>
  typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null;

/**
 * **HER SUCCESS RATE, AND ITS DENOMINATOR IS THE SETTLED SENDS.**
 *
 * `null` in three cases, all of them the same ruling: nothing has settled yet (a rate of an empty
 * population is not zero), or either count could not be read. A percentage is withheld rather than
 * guessed at.
 */
export function deliveryRate(delivered, failed) {
  const d = count(delivered);
  const f = count(failed);
  if (d === null || f === null) return null;
  const settled = d + f;
  if (settled <= 0) return null;
  return Math.round((d / settled) * 100);
}

/**
 * **ONE DAY, READ.** Takes the store's own row shape and answers her four numbers plus the waiting
 * count, or `null` for a row this cannot read.
 *
 * >>> THE DAY IS A PARTITION AND IT IS ASSERTED HERE RATHER THAN ASSUMED. <<< `delivered + failed +
 * waiting` must be exactly `attempted`: the three predicates come out of SQL and a fourth stamp, a
 * changed grace or a `NULL` in the wrong column would silently drop sends off the bottom of her
 * chart. A row that does not partition is UNREADABLE rather than drawn short (R19: a short chart
 * reads as a quiet week).
 */
export function pushDeliveryDay(raw = {}) {
  const day = String(raw?.day || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const delivered = count(raw?.delivered);
  const failed = count(raw?.failed);
  const waiting = count(raw?.waiting);
  const attempted = count(raw?.attempted);
  if (delivered === null || failed === null || waiting === null || attempted === null) return null;
  if (delivered + failed + waiting !== attempted) return null;
  return { day, attempted, delivered, failed, waiting, rate: deliveryRate(delivered, failed) };
}

/**
 * **THE WHOLE SERIES, NEWEST FIRST, AND WHAT IT COULD NOT READ.**
 *
 * >>> A DROPPED DAY IS NOT A SILENT CAP (R17/R160). <<< A row the law refuses is COUNTED and the
 * count rides out, so the surface can say a day is missing instead of drawing a chart that is
 * quietly short — which is the same failure as a zero: it reads as a quiet day rather than as an
 * instrument that could not answer.
 *
 * Newest first because the row she acts on is this morning's; a chronological chart puts it at the
 * bottom of the tallest table on the tab.
 */
export function pushDeliverySeries(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const days = [];
  let unreadable = 0;
  for (const r of list) {
    const d = pushDeliveryDay(r);
    if (d) days.push(d); else unreadable += 1;
  }
  days.sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
  return { days, unreadable };
}

/**
 * **THE PERIOD'S OWN FOUR NUMBERS, SUMMED FROM THE ROWS SHE IS LOOKING AT.**
 *
 * >>> THE TOTAL RATE IS RE-DERIVED FROM THE TOTALS AND IS NEVER AN AVERAGE OF THE DAILY RATES. <<<
 * A mean of percentages weights a day that sent four pushes exactly as heavily as a day that sent
 * four hundred, so a single quiet bad morning would drag the period's headline figure down by more
 * than it cost — a number that moves for a reason she cannot see on the rows under it.
 */
export function pushDeliveryTotals(days) {
  const list = Array.isArray(days) ? days : [];
  const sum = (k) => list.reduce((n, d) => n + (count(d?.[k]) || 0), 0);
  const delivered = sum("delivered");
  const failed = sum("failed");
  return {
    attempted: sum("attempted"),
    delivered,
    failed,
    waiting: sum("waiting"),
    rate: deliveryRate(delivered, failed),
  };
}

// **THE ONE SENTENCE THAT EXPLAINS A GAP SHE CAN SEE, AND IT SAYS NOTHING WHEN THERE IS NOTHING TO
// EXPLAIN.** A screen that keeps apologising is one she stops reading (SURFACE 2(b)'s own ruling),
// so this is empty unless sends really are still in flight — which is essentially only today's row,
// which is exactly when the difference between `attempted` and `delivered + failed` is visible and
// would otherwise read as arithmetic that does not add up.
export function pushWaitingNote(waiting, graceMinutes) {
  const w = count(waiting);
  const m = count(graceMinutes);
  if (!w || !m) return "";
  return `${w === 1 ? "1 send is" : `${w} sends are`} still inside the ${m}-minute wait, `
    + "so they are counted as neither delivered nor failed yet.";
}

// **AND A DAY THIS LAW COULD NOT READ SAYS SO IN HER WORDS, NEVER IN A COUNT SHE HAS TO INTERPRET.**
export function pushUnreadableNote(unreadable) {
  const n = count(unreadable);
  if (!n) return "";
  return `${n === 1 ? "One day" : `${n} days`} could not be read and ${n === 1 ? "is" : "are"} not in this table.`;
}
